import {
  Template,
  AssignTag,
  TokenKind,
  Value,
  CaptureTag,
  CaseTag,
  CommentTag,
  Output,
  IfTag,
  RawTag,
  UnlessTag,
  InlineCommentTag,
  ValueToken,
  Expression,
  Filter,
  Token,
} from "liquidjs";
import type {
  PropertyAccessToken,
  IdentifierToken,
} from "liquidjs/dist/tokens";

export type TemplateAnalytics = {
  tagsInUse: Set<string>;
  filtersInUse: Set<string>;
  variablesInUse: Set<string>;
  variablesAssignedTo: Set<string>;
};

export function analyzeTemplate(
  topLevelTemplates: Template[]
): TemplateAnalytics {
  const tagsInUse: Set<string> = new Set();
  const filtersInUse: Set<string> = new Set();
  const variablesInUse: Set<string> = new Set();
  const variablesAssignedTo: Set<string> = new Set();

  function handlePropertyAccessToken(token: PropertyAccessToken) {
    const variable = token.variable;
    if (variable?.kind === TokenKind.Word) {
      variablesInUse.add((variable as IdentifierToken).content);
    }

    if (token.props[0]?.kind === TokenKind.Word) {
      variablesInUse.add((token.props[0] as IdentifierToken).content);
    }
  }

  function handleFilter(filter: Filter) {
    filtersInUse.add(filter.name);
    filter.args.forEach(arg => {
      if (Array.isArray(arg)) {
        const [_name, maybeValue] = arg;
        if (maybeValue) {
          handleValueToken(maybeValue);
        }
      } else {
        handleValueToken(arg);
      }
    });
  }

  function handleExpression(expression: Expression) {
    const tokens: Token[] = (expression as any).postfix;
    tokens.forEach(token => {
      switch (token.kind) {
        case TokenKind.PropertyAccess:
          handlePropertyAccessToken(token as PropertyAccessToken);
          break;
      }
    });
  }

  function handleValue(value: Value) {
    handleExpression(value.initial);
    value.filters.forEach(handleFilter);
  }

  function handleValueToken(value: ValueToken) {
    switch (value.kind) {
      case TokenKind.Range:
      case TokenKind.Quoted:
      case TokenKind.Literal:
        break;
      case TokenKind.PropertyAccess: {
        handlePropertyAccessToken(value as PropertyAccessToken);
      }
    }
  }

  function handleTag(template: Template) {
    if (template instanceof AssignTag) {
      tagsInUse.add("assign");
      variablesAssignedTo.add((template as any).key);
      handleValue((template as any).value);
    } else if (template instanceof IfTag || template instanceof UnlessTag) {
      tagsInUse.add(template.name);
      template.branches.forEach(branch => {
        handleValue(branch.value);
        handleTopLevelTemplates(branch.templates);
      });
      handleTopLevelTemplates(template.elseTemplates || []);
    } else if (template instanceof CaseTag) {
      tagsInUse.add("case");
      handleValue(template.value);
      template.branches.forEach(branch => {
        branch.values.forEach(handleValueToken);
        handleTopLevelTemplates(branch.templates);
      });
      handleTopLevelTemplates(template.elseTemplates);
    } else if (
      template instanceof CommentTag ||
      template instanceof InlineCommentTag ||
      template instanceof RawTag
    ) {
      tagsInUse.add(template.name);
    } else if (template instanceof CaptureTag) {
      tagsInUse.add("capture");
      variablesAssignedTo.add(template.variable);
      handleTopLevelTemplates(template.templates);
    } else {
      throw new Error(`Unknown tag: ${template.token.getText()}`);
    }
  }

  function handleTopLevelTemplates(templates: Template[]) {
    templates.forEach(templatePart => {
      switch (templatePart.token.kind) {
        case TokenKind.Tag:
          handleTag(templatePart);
          break;
        case TokenKind.Output:
          handleValue((templatePart as Output).value);
          break;
        case TokenKind.HTML:
          // "HTML" means literal output, so we don't need to do anything with
          // that
          break;
        default:
          // We should only have top-level tokens here, so this shouldn't happen
          throw new Error("Unknown template part");
      }
    });
  }

  handleTopLevelTemplates(topLevelTemplates);

  return {
    tagsInUse,
    filtersInUse,
    variablesInUse,
    variablesAssignedTo,
  };
}
